// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
} from '@angular/core';
import { Observable, combineLatest, map, startWith } from 'rxjs';
import { SystemDetailsModel } from '../../../../api/remote-api/.portal.generated/models';
import { APICallGateComponent } from '../../../shared/components/api-call-gate.component';
import { Observe } from '../../../shared/utils/as-observable';
import { SystemOverviewService } from '../../services/systemOverview.service';

@Component({
  selector: 'app-mi-applications',
  templateUrl: './mi-applications.component.html',
  styleUrls: ['./mi-applications.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [NgIf, APICallGateComponent, NgFor, AsyncPipe],
})
export class MIApplications implements OnChanges {
  @Input({ required: false }) landingPageMode = false;
  @Input({ required: false }) modalityId?: number;
  @Input({ required: false }) systemDetails?: SystemDetailsModel;

  @Observe('systemDetails')
  private systemDetails$!: Observable<SystemDetailsModel>;
  @Observe('modalityId')
  private modalityId$!: Observable<number>;

  // has {generic, and system} list (generic is ignored)
  genericApps = this.systemOverviewService.miApplicationsForModality;
  // only has {generic} app list
  systemApps = this.systemOverviewService.miApplicationsForSystem;

  // set dynamically base on which app call we invoke
  apps$ = combineLatest([
    this.systemApps.lastSuccessfulData$.pipe(startWith(undefined)),
    this.genericApps.lastSuccessfulData$.pipe(startWith(undefined)),
  ]).pipe(
    map(([system, generic]) => {
      return this.hasSystemContext ? system : generic;
    })
  );

  // title is either systemName from details or modality name
  title$ = combineLatest([
    this.systemDetails$.pipe(startWith(undefined)),
    this.modalityId$.pipe(startWith(undefined)),
    this.systemOverviewService.modalities.lastSuccessfulData$,
  ]).pipe(
    map(([details, modalityId, modalities]) => {
      if (details) {
        return details.systemName;
      }
      if (modalityId && modalities) {
        // get modality display name
        return modalities.find(({ id }) => id === modalityId)?.displayName;
      }
    })
  );

  constructor(
    public readonly http: HttpClient,
    protected systemOverviewService: SystemOverviewService
  ) {}

  get hasSystemContext(): boolean {
    return !!this.systemDetails;
  }

  get usedAPICall(): typeof this.genericApps | typeof this.systemApps {
    return this.hasSystemContext ? this.systemApps : this.genericApps;
  }

  ngOnChanges(): void {
    if (this.hasSystemContext && this.systemDetails) {
      // in this case we show apps for this type of system
      this.systemApps.request(this.systemDetails);
      this.systemApps.request(this.systemDetails);
    } else if (this.modalityId) {
      this.genericApps.request({ modalityId: this.modalityId });
    }
  }
}
